import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Article 4 - 台灣 Omakase 文化發展
 * SEO 由 ArticlePage.jsx 集中管理，此組件只負責內容渲染
 * ✅ 已移除重複的 <Helmet>、重複 Article Schema、多餘 <article> 包裹層
 */
export default function Article4() {
  return (
    <div>
      {/* 引言 */}
      <section className="mb-12">
        <p className="text-lg leading-relaxed text-gray-700 dark:text-gray-300 mb-6">
          台灣與日本料理的緣分，可以追溯到日治時期。經過百年的發展，台灣的 Omakase 文化已經形成獨特的風貌——既保留了日本料理的精髓與嚴謹，又融入了台灣豐富的在地食材與創新精神。這種「台式 Omakase」不僅是對日本料理的致敬，更是一種文化的再創造與升華。
        </p>
      </section>

      {/* 歷史發展 */}
      <section className="mb-16">
        <h2 id="historical-development" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-green-500">
          台灣 Omakase 文化的歷史發展
        </h2>

        <div className="relative">
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-300 via-emerald-300 to-teal-300 dark:from-green-700 dark:via-emerald-700 dark:to-teal-700"></div>

          {[
            {
              id: 'japanese-era', color: 'green', title: '日治時期（1895-1945）：基礎奠定',
              desc: '日本統治時期，大量日本料理文化隨著移民進入台灣。當時的日本料理主要服務日籍官員和商人，食材多從日本進口。',
              cardColor: 'green', cardTitle: '重要影響',
              items: ['引入日本料理的基本技法和器具', '建立最早的日本料理餐廳', '培養第一代台籍日本料理廚師', '奠定台灣人對日本料理的認識基礎'],
            },
            {
              id: 'post-war-period', color: 'emerald', title: '戰後初期（1945-1970）：沉寂與保留',
              desc: '戰後初期，由於政治因素，日本料理文化一度受到壓抑。但仍有部分餐廳以「和食」或「東洋料理」的名義持續經營。',
              cardColor: 'emerald', cardTitle: '文化傳承',
              items: ['老師傅默默傳承技藝', '部分日籍廚師選擇留在台灣', '開始嘗試使用台灣在地食材', '台日料理技法開始融合'],
            },
            {
              id: 'economic-boom', color: 'teal', title: '經濟起飛期（1970-1990）：復興與發展',
              desc: '隨著台灣經濟快速發展，日本料理重新受到重視。高級日本料理餐廳開始出現，但仍以傳統日式為主。',
              cardColor: 'teal', cardTitle: '關鍵發展',
              items: ['第一批赴日學藝的台灣廚師返台', '高級日本料理餐廳興起', '開始進口日本高級食材', '台灣食客對日本料理的認識加深'],
            },
            {
              id: 'innovation-period', color: 'cyan', title: '創新融合期（1990-2010）：在地化萌芽',
              desc: '台灣廚師開始有意識地將在地食材融入日本料理，「台式日本料理」概念逐漸成形。Omakase 形式開始在台灣流行。',
              cardColor: 'cyan', cardTitle: '創新突破',
              items: ['台灣在地食材被系統性運用', 'Omakase 形式在台灣普及', '台灣廚師開始建立個人風格', '台日食材混搭成為特色'],
            },
            {
              id: 'modern-era', color: 'blue', title: '現代發展（2010-現在）：獨特風格確立',
              desc: '台灣 Omakase 文化進入成熟期，形成獨特的「台式 Omakase」風格，在國際上獲得認可，甚至出現米其林星級餐廳。',
              cardColor: 'blue', cardTitle: '現況特色',
              items: ['台灣 Omakase 風格被國際認可', '多家餐廳獲得米其林星級', '年輕廚師帶來更多創新', '在地食材成為競爭優勢', '價格區間多元化，普及度提高'],
            },
          ].map((period, idx) => (
            <div key={period.id} className={`relative ${idx < 4 ? 'mb-12' : ''} ml-16`}>
              <div className={`absolute -left-[2.3rem] top-2 w-6 h-6 bg-${period.color}-500 rounded-full border-4 border-white dark:border-gray-900`}></div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                <h3 id={period.id} className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{period.title}</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">{period.desc}</p>
                <div className={`bg-${period.cardColor}-50 dark:bg-${period.cardColor}-900/20 p-4 rounded-lg`}>
                  <h4 className={`font-bold text-${period.cardColor}-600 dark:text-${period.cardColor}-400 mb-2`}>{period.cardTitle}</h4>
                  <ul className="text-sm space-y-1 ml-4 text-gray-700 dark:text-gray-300">
                    {period.items.map(i => <li key={i}>• {i}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 在地食材 */}
      <section className="mb-16">
        <h2 id="local-ingredients" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-green-500">
          台灣在地食材的創新運用
        </h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
          台灣得天獨厚的地理環境和豐富的海洋資源，為 Omakase 提供了獨特的食材優勢。台灣廚師將這些在地食材與日本料理技法完美結合，創造出獨樹一格的風味。
        </p>
        <div className="grid md:grid-cols-2 gap-8 mb-10">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
            <h3 id="seafood-ingredients" className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="text-3xl">🐟</span>海鮮類
            </h3>
            <div className="space-y-4">
              {[
                { color: 'blue', name: '東港黑鮪魚', desc: '每年 5-6 月的東港黑鮪魚季是台灣 Omakase 的重要時刻。', items: ['品質媲美日本大間鮪魚', '新鮮度極佳，當日捕撈當日使用', '價格相對親民', '成為台灣 Omakase 的招牌'] },
                { color: 'cyan', name: '野生午仔魚', desc: '台灣西南海域的特產，肉質細嫩，油脂豐富。', items: ['適合握壽司和炙燒', '風味獨特，日本少見', '展現台灣海域特色'] },
                { color: 'teal', name: '澎湖海膽', desc: '澎湖海域的紫海膽，品質優異，甜度高。', items: ['鮮甜度不輸北海道海膽', '產季為 4-8 月', '成為台灣 Omakase 亮點'] },
                { color: 'green', name: '宜蘭櫻花蝦', desc: '宜蘭海域捕獲的櫻花蝦，有獨特風味。', items: ['在地替代方案', '新鮮度極佳', '價格較親民'] },
              ].map(item => (
                <div key={item.name} className={`bg-${item.color}-50 dark:bg-${item.color}-900/20 p-4 rounded-xl`}>
                  <h4 className={`font-bold text-${item.color}-600 dark:text-${item.color}-400 mb-2`}>{item.name}</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{item.desc}</p>
                  <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 ml-4">
                    {item.items.map(i => <li key={i}>• {i}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
            <h3 id="meat-ingredients" className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="text-3xl">🥩</span>肉類與其他
            </h3>
            <div className="space-y-4">
              {[
                { color: 'red', name: '台灣和牛', desc: '近年台灣自產和牛品質大幅提升，成為 Omakase 新寵。', items: ['油花分布均勻', '風味不輸日本和牛', '新鮮度優勢明顯', '用於炙燒握壽司或牛肉料理'] },
                { color: 'orange', name: '宜蘭櫻桃鴨', desc: '高品質的櫻桃鴨，適合製作創意料理。', items: ['肉質細嫩，油脂豐富', '適合煙燻、炙燒等技法', '展現台式創意'] },
                { color: 'yellow', name: '阿里山竹筍', desc: '高海拔種植的竹筍，清甜脆嫩，品質優異。', items: ['春季限定', '可媲美日本京都竹筍', '用於煮物、炭烤等'] },
                { color: 'lime', name: '台東釋迦', desc: '創意甜點的新選擇，展現台灣水果的優勢。', items: ['製作創意甜品', '與日式技法結合', '獨特的台灣風味'] },
              ].map(item => (
                <div key={item.name} className={`bg-${item.color}-50 dark:bg-${item.color}-900/20 p-4 rounded-xl`}>
                  <h4 className={`font-bold text-${item.color}-600 dark:text-${item.color}-400 mb-2`}>{item.name}</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{item.desc}</p>
                  <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 ml-4">
                    {item.items.map(i => <li key={i}>• {i}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 台日廚師比較 */}
      <section className="mb-16">
        <h2 id="chef-comparison" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-green-500">
          台灣與日本廚師的差異
        </h2>
        <div className="overflow-x-auto mb-8">
          <table className="w-full border-collapse bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg">
            <thead>
              <tr className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                <th className="p-4 text-left font-bold">比較項目</th>
                <th className="p-4 text-left font-bold">日本廚師</th>
                <th className="p-4 text-left font-bold">台灣廚師</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {[
                { dim: '訓練體系', jp: '嚴格的師徒制，10-15年修業', tw: '多元學習管道，時間較彈性' },
                { dim: '料理哲學', jp: '強調傳統、純粹、不變', tw: '保留傳統但勇於創新' },
                { dim: '食材選擇', jp: '日本產地為主，重視產地認證', tw: '日本+台灣食材混搭' },
                { dim: '創新程度', jp: '保守，變化緩慢', tw: '較為開放，接受新想法' },
                { dim: '與客人互動', jp: '較為正式、有距離感', tw: '較為親切、輕鬆自然' },
                { dim: '價格定位', jp: '高端為主，價格較高', tw: '價格區間更廣，更親民' },
                { dim: '優勢', jp: '技術精湛、傳統道地', tw: '創意豐富、在地食材優勢' },
              ].map(row => (
                <tr key={row.dim} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="p-4 font-medium text-gray-900 dark:text-white">{row.dim}</td>
                  <td className="p-4 text-gray-700 dark:text-gray-300">{row.jp}</td>
                  <td className="p-4 text-gray-700 dark:text-gray-300">{row.tw}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 餐廳類型 */}
      <section className="mb-16">
        <h2 id="restaurant-types" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-green-500">
          台灣特色 Omakase 餐廳類型
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { id: 'traditional-style', icon: '🏯', title: '傳統派', desc: '堅持日本傳統，食材以日本進口為主，力求原汁原味。', items: ['日籍廚師或留日廚師主理', '90% 以上食材來自日本', '嚴格遵循日本料理規範', '價格通常較高'], note: '適合：追求最道地日本 Omakase 體驗' },
            { id: 'fusion-style', icon: '🌟', title: '融合派', desc: '日本技法+台灣食材，創造獨特的台式 Omakase 風格。', items: ['台灣廚師為主', '日本+台灣食材並重', '勇於創新和實驗', '價格較為合理'], note: '代表：目前台灣最主流的類型' },
            { id: 'local-style', icon: '🇹🇼', title: '在地派', desc: '以台灣在地食材為主，展現台灣海鮮和農產的優勢。', items: ['80% 以上使用台灣食材', '強調產地直送和新鮮度', '展現台灣四季特色', '建立台灣 Omakase 認同'], note: '代表：新興趨勢，越來越受重視' },
            { id: 'creative-style', icon: '🎨', title: '創意派', desc: '打破傳統框架，融入西式或其他料理元素。', items: ['年輕廚師為主', '融入分子料理、法式技法等', '視覺呈現更為大膽', '適合追求新鮮感的客人'], note: '代表：台灣 Omakase 的未來可能性' },
          ].map(type => (
            <div key={type.id} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
              <h3 id={type.id} className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <span className="text-2xl">{type.icon}</span>{type.title}
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">{type.desc}</p>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                  {type.items.map(i => <li key={i}>• {i}</li>)}
                </ul>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 italic">{type.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 未來趨勢 */}
      <section className="mb-16">
        <h2 id="future-trends" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-green-500">
          台灣 Omakase 的未來趨勢
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { id: 'sustainability-trend', icon: '🌱', title: '永續發展', desc: '更多餐廳開始重視永續海鮮、有機食材和減少食物浪費。', items: ['選擇永續捕撈的海鮮', '支持小農和有機農業', '減少一次性餐具使用', '食材全利用，減少浪費'] },
            { id: 'innovation-trend', icon: '💡', title: '技術創新', desc: '年輕廚師帶來更多創新技法和跨界合作。', items: ['融入分子料理技術', '跨界與西餐、法餐合作', '運用現代烹飪設備', '創新擺盤和視覺呈現'] },
            { id: 'localization-trend', icon: '🏡', title: '在地化深化', desc: '更深入挖掘台灣在地食材和文化元素。', items: ['發掘更多在地特色食材', '建立與產地的直接連結', '融入台灣文化元素', '建立台灣 Omakase 品牌'] },
            { id: 'democratization-trend', icon: '💰', title: '平民化', desc: 'Omakase 不再是高不可攀，出現更多親民價位。', items: ['午間 Omakase 套餐', '小型吧台餐廳增加', '價格更透明化', '降低體驗門檻'] },
            { id: 'internationalization-trend', icon: '🌏', title: '國際化', desc: '台灣 Omakase 逐漸在國際上獲得認可。', items: ['米其林星級餐廳增加', '國際美食評鑑肯定', '吸引國際饕客來訪', '台灣廚師海外展店'] },
            { id: 'digitalization-trend', icon: '📱', title: '數位化', desc: '運用科技提升預約和用餐體驗。', items: ['線上預約系統完善', '社群媒體行銷', '數位菜單和介紹', '客戶關係管理'] },
          ].map(trend => (
            <div key={trend.id} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
              <div className="text-4xl mb-4">{trend.icon}</div>
              <h3 id={trend.id} className="text-xl font-bold text-gray-900 dark:text-white mb-3">{trend.title}</h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{trend.desc}</p>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {trend.items.map(i => <li key={i}>• {i}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* 結語 */}
      <section className="mb-16">
        <h2 id="conclusion" className="text-3xl font-bold text-gray-900 dark:text-white mb-6">結語</h2>
        <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 p-8 rounded-2xl">
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            台灣的 Omakase 文化經過數十年的發展，已經從單純的模仿日本，演變為具有獨特風格的「台式 Omakase」。這種發展不是對日本料理的背離，而是一種創造性的傳承與創新。
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            台灣廚師在保持對日本料理傳統的尊重的同時，勇於運用台灣豐富的在地食材，創造出既有日本料理精髓，又帶有台灣特色的料理體驗。這種「尊重傳統，勇於創新」的精神，正是台灣 Omakase 文化最珍貴的資產。
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            未來，台灣 Omakase 將繼續在傳統與創新之間找到平衡，在全球化與在地化之間建立獨特定位，為世界呈現一個更多元、更豐富的日本料理面貌。
          </p>
        </div>
      </section>

      {/* 延伸閱讀 */}
      <section className="mt-16 pt-8 border-t-2 border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">延伸閱讀</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { to: '/beginners-complete-guide-to-omakase', icon: '📚', title: '初學者的 Omakase 完整指南', desc: '從零開始了解 Omakase 的基礎知識', gradient: 'from-orange-400 to-rose-500', hover: 'orange' },
            { to: '/premium-ingredients-analysis-from-tsukiji', icon: '🐟', title: '頂級食材解析', desc: '深入了解 Omakase 使用的頂級食材', gradient: 'from-blue-400 to-cyan-500', hover: 'blue' },
            { to: '/sushi-vs-kaiseki-omakase-comparison', icon: '🍱', title: '壽司 vs 懷石 Omakase', desc: '比較兩種不同風格的 Omakase', gradient: 'from-purple-400 to-pink-500', hover: 'purple' },
          ].map(link => (
            <Link key={link.to} to={link.to} className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className={`h-48 bg-gradient-to-br ${link.gradient} flex items-center justify-center`}>
                <span className="text-6xl">{link.icon}</span>
              </div>
              <div className="p-6">
                <h3 className={`text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-${link.hover}-500 transition-colors`}>{link.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{link.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}